import { useCallback } from 'react';
import { api } from '../api/client';
import { useAuthStore } from '../store/authStore';
import type {
  LoginRequest, RegisterRequest, AuthResponse, RegisterResponse,
  PasswordResetRequest, PasswordResetConfirmRequest, MessageResponse,
} from '../types/reunion';

export function useAuth() {
  const { token, user, roles, isAuthenticated, setAuth, clearAuth } = useAuthStore();

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    setAuth(response.token, response.user, response.roles);
    return response;
  }, [setAuth]);

  const register = useCallback(async (data: RegisterRequest) => {
    const response = await api.post<RegisterResponse>('/auth/register', data);
    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post<MessageResponse>('/auth/logout');
    } catch {
      // Logout should succeed even if the API call fails
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const requestPasswordReset = useCallback(async (data: PasswordResetRequest) => {
    return api.post<MessageResponse>('/auth/reset-password', data);
  }, []);

  const confirmPasswordReset = useCallback(async (data: PasswordResetConfirmRequest) => {
    return api.post<MessageResponse>('/auth/reset-confirm', data);
  }, []);

  const verifyEmail = useCallback(async (verifyToken: string) => {
    return api.get<MessageResponse>('/auth/verify-email', { token: verifyToken });
  }, []);

  const hasRole = useCallback((roleSlug: string, reunionId?: string) => {
    return roles.some(r =>
      r.roleSlug === roleSlug && (reunionId ? r.reunionId === reunionId : true)
    );
  }, [roles]);

  const isAdmin = useCallback(() => {
    return roles.some(r => r.roleSlug === 'super_admin' || r.roleSlug === 'reunion_admin');
  }, [roles]);

  return {
    // State
    token,
    currentUser: user,
    roles,
    isAuthenticated,

    // Actions
    login,
    register,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    verifyEmail,

    // Role helpers
    hasRole,
    isAdmin,
  };
}
